import { useState, useEffect } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'
import { clsx } from 'clsx'

import {
  useSplitEnabled,
  setCachedTreatment,
  getCachedTreatment,
} from '@buffer-mono/features'
import {
  Flex,
  Heading,
  Separator,
  Form,
  Switch,
  Text,
  Button,
  Skeleton,
  IconButton,
  toast,
  Badge,
  Notice,
  MessageCircleHeartIcon,
  FlaskConicalIcon,
  WarningIcon,
  DiscordIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { Markdown } from '~publish/components/Markdown'
import {
  useAccount,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { env } from '~publish/env'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { useAlphaFeatures } from './hooks/useAlphaFeatures'
import type { AlphaFeature } from './hooks/useAlphaFeatures'
import { useBetaFeatures } from './hooks/useBetaFeatures'
import type { BetaFeature } from './hooks/useBetaFeatures'
import {
  RELOAD_TOAST_OPTIONS,
  useUpdateBetaFeaturesEnrolment,
} from './hooks/useUpdateBetaFeaturesEnrolment'
import { getHelloNextLoginServiceUrl } from './utils/helloNextLoginServiceUrl'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsRow } from '../shared/components/SettingsRow'

import styles from './BetaFeatures.module.css'

const handleCheckRoadmap = (): void => {
  window.open('https://suggestions.buffer.com/roadmap', '_blank')
}

type EnrichedFeature = (BetaFeature | AlphaFeature) & {
  treatment: string
  isOptedOut: boolean
  canToggle: boolean
  propagationStatus: 'idle' | 'pending'
}

// Pending intent system — trusts user intent for up to 3 minutes while Split.io propagates
const PENDING_INTENT_TTL_MS = 3 * 60 * 1000
const PENDING_INTENT_PREFIX = 'beta-pending/'

type PendingIntent = { treatment: string; timestamp: number }

function pendingIntentKey(orgId: string, splitName: string): string {
  return `${PENDING_INTENT_PREFIX}${orgId}:${splitName}`
}

export function setPendingIntent(
  orgId: string,
  splitName: string,
  treatment: string,
): void {
  try {
    const value: PendingIntent = { treatment, timestamp: Date.now() }
    localStorage.setItem(
      pendingIntentKey(orgId, splitName),
      JSON.stringify(value),
    )
  } catch {
    // Ignore localStorage errors
  }
}

function isPendingIntent(value: unknown): value is PendingIntent {
  if (typeof value !== 'object' || value === null) return false
  if (!('treatment' in value) || !('timestamp' in value)) return false
  return (
    typeof value.treatment === 'string' && typeof value.timestamp === 'number'
  )
}

export function getPendingIntent(
  orgId: string,
  splitName: string,
): PendingIntent | null {
  try {
    const raw = localStorage.getItem(pendingIntentKey(orgId, splitName))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (isPendingIntent(parsed)) {
      if (Date.now() - parsed.timestamp > PENDING_INTENT_TTL_MS) {
        localStorage.removeItem(pendingIntentKey(orgId, splitName))
        return null
      }
      return parsed
    }
    return null
  } catch {
    return null
  }
}

export function clearPendingIntent(orgId: string, splitName: string): void {
  try {
    localStorage.removeItem(pendingIntentKey(orgId, splitName))
  } catch {
    // Ignore localStorage errors
  }
}

export function clearAllPendingIntents(orgId: string): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(`${PENDING_INTENT_PREFIX}${orgId}:`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // Ignore localStorage errors
  }
}

// Alpha enrollment tracking - stores when a user was enrolled in an alpha feature
// This persists even after they toggle OFF, allowing them to re-enable
const ALPHA_ENROLLED_PREFIX = 'split.io/alpha-enrolled/'

export function setAlphaEnrolled(splitName: string): void {
  try {
    localStorage.setItem(`${ALPHA_ENROLLED_PREFIX}${splitName}`, 'true')
  } catch {
    // Ignore localStorage errors (privacy mode, etc.)
  }
}

export function isAlphaEnrolled(splitName: string): boolean {
  try {
    return (
      localStorage.getItem(`${ALPHA_ENROLLED_PREFIX}${splitName}`) === 'true'
    )
  } catch {
    return false
  }
}

const SetBetaFeatureState = graphql(/* GraphQL */ `
  mutation SetBetaFeatureState($input: SetBetaFeatureStateInput!) {
    setBetaFeatureState(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
    }
  }
`)

const EnableBetaFeatures = ({
  isBetaEnabled,
  organizationId,
  betaEnabledInner,
  enrollmentPending,
  disabled,
  onBetaEnabledChange,
}: {
  isBetaEnabled?: boolean | null
  organizationId: string
  betaEnabledInner: boolean
  enrollmentPending: boolean
  disabled: boolean
  onBetaEnabledChange: (enabled: boolean) => void
}): JSX.Element => {
  const { enableBetaFeatures, disableBetaFeatures } =
    useUpdateBetaFeaturesEnrolment()

  const handleSwitchChange = (checked: boolean): void => {
    onBetaEnabledChange(checked)
    void (async (): Promise<void> => {
      try {
        if (checked) {
          await enableBetaFeatures({ organizationId })
        } else {
          await disableBetaFeatures({ organizationId })
          clearAllPendingIntents(organizationId)
        }
      } catch {
        onBetaEnabledChange(!checked)
      }
    })()
  }

  return (
    <Flex
      gap="xs"
      direction="column"
      fullWidth
      className={styles.enableBetaFeaturesWrapper}
    >
      <SettingsRow id="enable-beta-features">
        <SettingsRow.Header>
          <SettingsRow.Title
            className={styles.noWrapHeading}
            icon={
              <FlaskConicalIcon
                className={`${styles.sectionIcon} ${styles.betaIcon}`}
              />
            }
          >
            Enable Beta Features
          </SettingsRow.Title>
          <SettingsRow.Description>
            Fully functional features we&apos;re testing before release.
          </SettingsRow.Description>
        </SettingsRow.Header>
        <SettingsRow.Control inline>
          {(ids) => (
            <Form>
              <Skeleton
                show={isBetaEnabled === null || isBetaEnabled === undefined}
              >
                <Form.Field name="betaFeatures">
                  <Form.Control>
                    <Switch
                      checked={betaEnabledInner}
                      disabled={disabled}
                      onCheckedChange={handleSwitchChange}
                      aria-labelledby={ids.headingId}
                      aria-describedby={ids.descriptionId}
                    />
                  </Form.Control>
                </Form.Field>
              </Skeleton>
            </Form>
          )}
        </SettingsRow.Control>
      </SettingsRow>
      {enrollmentPending && (
        <Notice variant="info" className={styles.enrollmentNotice}>
          <Notice.Text>
            {betaEnabledInner ? 'Activating' : 'Deactivating'} beta features...
            This can take a couple of minutes.
          </Notice.Text>
        </Notice>
      )}
    </Flex>
  )
}

interface FeatureListProps {
  features: EnrichedFeature[]
  isBufferTeam: boolean
  isDisabled: boolean
  onShareFeedback: (feature: EnrichedFeature) => void
  onFeatureToggle: (splitName: string, enabled: boolean) => Promise<void>
}

const FeatureList = ({
  features,
  isBufferTeam,
  isDisabled,
  onShareFeedback,
  onFeatureToggle,
}: FeatureListProps): JSX.Element => {
  return (
    <SettingsRow.Nested>
      {features.map((feature) => (
        <SettingsRow
          id={`beta-feature-${feature.splitName}`}
          key={feature.name}
          className={isBufferTeam ? styles.featureRow : undefined}
        >
          <SettingsRow.Header>
            {(ids) => (
              <>
                <SettingsRow.Title
                  className={clsx(
                    styles.featureTitle,
                    feature.treatment === 'on' && styles.featureTitleEnabled,
                  )}
                >
                  {feature.name}
                  {feature.propagationStatus === 'pending' ? (
                    <Badge
                      size="small"
                      variant="neutral"
                      className={styles.pendingBadge}
                    >
                      {feature.treatment === 'on'
                        ? 'Activating...'
                        : 'Deactivating...'}
                    </Badge>
                  ) : (
                    feature.isOptedOut && ' (Disabled)'
                  )}
                  {isBufferTeam && (
                    <Badge
                      size="small"
                      variant="neutral"
                      className={styles.splitNameBadge}
                    >
                      {feature.splitName}: {feature.treatment}
                    </Badge>
                  )}
                </SettingsRow.Title>
                <Markdown
                  id={ids.descriptionId}
                  className={clsx(
                    styles.featureDescription,
                    feature.treatment === 'on' &&
                      styles.featureDescriptionEnabled,
                  )}
                >
                  {feature.description}
                </Markdown>
                {feature.propagationStatus === 'pending' && (
                  <Text color="subtle" className={styles.pendingMessage}>
                    This can take a couple of minutes.
                  </Text>
                )}
              </>
            )}
          </SettingsRow.Header>
          <SettingsRow.Control inline>
            {(ids) => (
              <>
                {isBufferTeam && feature.feedbackUrl ? (
                  <IconButton
                    variant="tertiary"
                    label="Share Feedback"
                    tooltip="Share Feedback"
                    onClick={(): void => onShareFeedback(feature)}
                  >
                    <MessageCircleHeartIcon />
                  </IconButton>
                ) : (
                  <FeedbackWidget
                    id={`beta-feature-${feature.splitName}`}
                    source="beta-features"
                  >
                    <IconButton
                      variant="tertiary"
                      label="Share Feedback"
                      tooltip="Share Feedback"
                    >
                      <MessageCircleHeartIcon />
                    </IconButton>
                  </FeedbackWidget>
                )}
                {feature.canToggle &&
                  // Wrap in FeedbackWidget when feature is ON + (external user OR no feedbackUrl)
                  (feature.treatment === 'on' &&
                  (!isBufferTeam || !feature.feedbackUrl) ? (
                    <FeedbackWidget
                      id={`beta-feature-disable-${feature.splitName}`}
                      source="beta-features"
                      onSubmit={(): void => {
                        void onFeatureToggle(feature.splitName, false)
                      }}
                      onClose={(): void => {
                        void onFeatureToggle(feature.splitName, false)
                      }}
                    >
                      <Switch
                        size={ids.controlSize}
                        data-state={
                          feature.treatment === 'on' ? 'checked' : 'unchecked'
                        }
                        checked={feature.treatment === 'on'}
                        disabled={
                          isDisabled || feature.propagationStatus === 'pending'
                        }
                        aria-labelledby={ids.headingId}
                        aria-describedby={ids.descriptionId}
                      />
                    </FeedbackWidget>
                  ) : (
                    <Switch
                      size={ids.controlSize}
                      checked={feature.treatment === 'on'}
                      disabled={
                        isDisabled || feature.propagationStatus === 'pending'
                      }
                      onCheckedChange={(checked): void => {
                        void onFeatureToggle(feature.splitName, checked)
                      }}
                      aria-labelledby={ids.headingId}
                      aria-describedby={ids.descriptionId}
                    />
                  ))}
              </>
            )}
          </SettingsRow.Control>
        </SettingsRow>
      ))}
    </SettingsRow.Nested>
  )
}

function enrichFeatures(
  features: Array<BetaFeature | AlphaFeature>,
  options: {
    localSplitStates: Record<string, string>
    isBufferTeam: boolean
    isBetaEnabled: boolean
    orgId: string
  },
): EnrichedFeature[] {
  return features.map<EnrichedFeature>((feature) => {
    const pending = getPendingIntent(options.orgId, feature.splitName)
    const treatment = pending
      ? pending.treatment
      : options.localSplitStates[feature.splitName] || 'off'
    const isOptedOut = treatment === 'off'
    const canToggle = options.isBetaEnabled || options.isBufferTeam
    const propagationStatus = pending ? 'pending' : 'idle'

    return { ...feature, treatment, isOptedOut, canToggle, propagationStatus }
  })
}

const BetaFeatures = (): JSX.Element => {
  const { account, loading: accountLoading } = useAccount()
  const canEditOrganization = useHasOrganizationAction('edit')
  // Allowed actions read as empty until the account query resolves, so the page
  // must not claim the actor is restricted before then — that would flash the
  // notice and grey every toggle for an admin on each load.
  const isReadOnly = !accountLoading && !canEditOrganization
  const { betaFeatures, splits } = useBetaFeatures()
  const { alphaFeatures, splits: alphaSplits } = useAlphaFeatures()
  const isBetaFeatureEnabled = account?.currentOrganization?.isBetaEnabled
  const organizationId = account?.currentOrganization?.id ?? ''
  const hasVerifiedEmail = Boolean(account?.hasVerifiedEmail)
  const { isEnabled: isBufferTeam } = useSplitEnabled('is-buffer-team')
  const { isEnabled: betaProgramEnabled, isReady: betaProgramReady } =
    useSplitEnabled('beta-program-customers')
  const [setBetaFeatureStateMutation] = useMutation(SetBetaFeatureState)

  // Local state to manage toggles (for responsiveness)
  const [localSplitStates, setLocalSplitStates] = useState<
    Record<string, string>
  >({})

  // Hoisted beta enabled state
  const [betaEnabledInner, setBetaEnabledInner] =
    useState(!!isBetaFeatureEnabled)

  // Priority: 1) Manual override, 2) Fresh cache (< 30s), 3) Split.io treatment
  useEffect(() => {
    const initialStates: Record<string, string> = {}

    // Type guard for cached storage values
    const hasCachedAt = (value: unknown): value is { cachedAt: number } =>
      typeof value === 'object' &&
      value !== null &&
      'cachedAt' in value &&
      typeof value.cachedAt === 'number'

    // Takes the raw storage value to avoid duplicate localStorage reads
    const isFreshCache = (
      storageValue: string,
      cached: ReturnType<typeof getCachedTreatment>,
    ): boolean => {
      if (cached?.type !== 'cache' || cached.expired) return false

      try {
        const parsed: unknown = JSON.parse(storageValue)
        if (!hasCachedAt(parsed)) return false
        const age = Date.now() - parsed.cachedAt
        return age < 30000
      } catch {
        return false
      }
    }

    // Resolves treatment with priority: 1) override, 2) fresh cache, 3) pending intent, 4) split treatment
    const resolveTreatment = (
      splitName: string,
      split: { treatment?: string } | undefined,
    ): string | undefined => {
      let storageValue: string | null = null
      try {
        storageValue = localStorage.getItem(`split.io/${splitName}`)
      } catch {
        // localStorage may be unavailable in privacy mode or restricted contexts
      }
      const cached = getCachedTreatment(splitName)

      if (cached?.type === 'override') {
        return cached.treatment
      } else if (
        storageValue &&
        cached?.type === 'cache' &&
        isFreshCache(storageValue, cached)
      ) {
        return cached.treatment.treatment
      }

      const pending = getPendingIntent(organizationId, splitName)
      if (pending) {
        return pending.treatment
      }

      if (split?.treatment) {
        return split.treatment
      }
      return undefined
    }

    // Add beta and alpha feature states
    ;[...Object.entries(splits), ...Object.entries(alphaSplits)].forEach(
      ([splitName, split]) => {
        const treatment = resolveTreatment(splitName, split)
        if (treatment) initialStates[splitName] = treatment
      },
    )

    setLocalSplitStates(initialStates)
  }, [splits, alphaSplits, isBufferTeam, organizationId])

  // Derive enrollment pending from Split.io propagation status.
  // Guard on isBetaFeatureEnabled being loaded — betaEnabledInner initialises as
  // false before the account resolves, which would produce a false "Deactivating"
  // notice on first render for users already in beta.
  const enrollmentPending =
    isBetaFeatureEnabled !== undefined &&
    betaProgramReady &&
    betaEnabledInner !== betaProgramEnabled

  useEffect(() => {
    setBetaEnabledInner(!!isBetaFeatureEnabled)
  }, [isBetaFeatureEnabled])

  // Convergence detection — clear pending intents when Split.io catches up
  useEffect(() => {
    const allSplits = { ...splits, ...alphaSplits }
    for (const splitName of Object.keys(allSplits)) {
      const pending = getPendingIntent(organizationId, splitName)
      if (pending && allSplits[splitName]?.treatment === pending.treatment) {
        clearPendingIntent(organizationId, splitName)
      }
    }
  }, [splits, alphaSplits, organizationId])

  // Track alpha enrollment - when user has treatment 'on', store a flag
  // This allows them to see the feature even after toggling OFF (for re-enablement)
  useEffect(() => {
    alphaFeatures?.forEach((feature) => {
      const treatment = localSplitStates[feature.splitName]
      if (treatment === 'on') {
        setAlphaEnrolled(feature.splitName)
      }
    })
  }, [alphaFeatures, localSplitStates])
  const enrichedBetaFeatures = enrichFeatures(betaFeatures || [], {
    localSplitStates,
    isBufferTeam,
    isBetaEnabled: !!isBetaFeatureEnabled,
    orgId: organizationId,
  }).filter(
    (feature) =>
      isBufferTeam || feature.treatment === 'on' || feature.isOptedOut,
  )

  const enrichedAlphaFeatures = enrichFeatures(alphaFeatures || [], {
    localSplitStates,
    isBufferTeam,
    isBetaEnabled: !!isBetaFeatureEnabled,
    orgId: organizationId,
  }).filter(
    (feature) =>
      isBufferTeam ||
      feature.treatment === 'on' ||
      isAlphaEnrolled(feature.splitName),
  )

  const showBetaSection = enrichedBetaFeatures.length > 0
  const showAlphaSection = enrichedAlphaFeatures.length > 0
  const showAboutSection = showBetaSection || showAlphaSection

  const handleShareFeedback = (feature: EnrichedFeature): void => {
    if (env.VITE_NODE_ENV === 'development') {
      window.open(feature.feedbackUrl ?? '', '_blank')
      return
    }
    window.open(
      hasVerifiedEmail
        ? `${getHelloNextLoginServiceUrl()}&redirect=${encodeURIComponent(
            feature.feedbackUrl ?? '',
          )}`
        : `${feature.feedbackUrl}`,
      '_blank',
    )
  }

  const handleFeatureToggle = async (
    splitName: string,
    enabled: boolean,
  ): Promise<void> => {
    if (!organizationId) {
      logError(new Error('No organization ID available'), {
        metaData: { splitName, enabled },
      })
      return
    }

    // Find the feature name for the toast
    const feature = [...(betaFeatures || []), ...(alphaFeatures || [])].find(
      (f) => f.splitName === splitName,
    )
    const featureName = feature?.name || splitName

    // Optimistic UI update while the mutation is in flight
    setLocalSplitStates((prev) => ({
      ...prev,
      [splitName]: enabled ? 'on' : 'off',
    }))

    try {
      await setBetaFeatureStateMutation({
        variables: {
          input: {
            organizationId,
            name: splitName,
            enabled,
          },
        },
      })

      // Write to cache for page reloads during Split.io propagation (2-5s window)
      // force=true bypasses CACHEABLE_SPLITS allowlist for explicit user toggles
      const newTreatment = enabled ? 'on' : 'off'
      setCachedTreatment(splitName, newTreatment, null, true)

      setPendingIntent(organizationId, splitName, newTreatment)

      toast.success(
        `${featureName} has been ${enabled ? 'enabled' : 'disabled'}.`,
        RELOAD_TOAST_OPTIONS,
      )
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        metaData: { splitName, enabled, organizationId },
      })

      toast.error(
        `There was an error ${
          enabled ? 'enabling' : 'disabling'
        } that feature. Please try again.`,
      )

      const originalTreatment =
        splits[splitName]?.treatment ||
        alphaSplits[splitName]?.treatment ||
        'off'

      setLocalSplitStates((prev) => ({
        ...prev,
        [splitName]: originalTreatment,
      }))
    }
  }

  const handleJoinDiscord = (): void => {
    BufferTracker.joinCommunityButtonClicked({
      clientName: 'core',
      product: 'publish',
      organizationId,
    })
    window.open('https://discord.com/invite/aQdKKr6kDY', '_blank')
  }

  return (
    <NewSettingsPageLayout title="Beta Features">
      {isReadOnly && (
        <Notice variant="info">
          <Notice.Text>
            Only an organization admin can change these settings.
          </Notice.Text>
        </Notice>
      )}
      <EnableBetaFeatures
        isBetaEnabled={isBetaFeatureEnabled}
        organizationId={organizationId}
        betaEnabledInner={betaEnabledInner}
        enrollmentPending={enrollmentPending}
        disabled={isReadOnly}
        onBetaEnabledChange={setBetaEnabledInner}
      />
      {showBetaSection && (
        <FeatureList
          features={enrichedBetaFeatures}
          isBufferTeam={isBufferTeam}
          isDisabled={isReadOnly || !betaEnabledInner || enrollmentPending}
          onShareFeedback={handleShareFeedback}
          onFeatureToggle={handleFeatureToggle}
        />
      )}
      {showAlphaSection && (
        <>
          <Separator />
          <Heading
            as="h2"
            size="small"
            className={styles.noWrapHeading}
            icon={
              <WarningIcon
                className={`${styles.sectionIcon} ${styles.alphaIcon}`}
              />
            }
          >
            Experimental Features
          </Heading>
          <Text color="subtle" className={styles.alphaDescription}>
            Early ideas we&apos;re exploring. They might change, break, or not
            make it to release.
          </Text>
          <FeatureList
            features={enrichedAlphaFeatures}
            isBufferTeam={isBufferTeam}
            isDisabled={isReadOnly}
            onShareFeedback={handleShareFeedback}
            onFeatureToggle={handleFeatureToggle}
          />
        </>
      )}
      {showAboutSection && (
        <>
          <Separator />
          <Flex gap="lg" direction="column" fullWidth>
            <Heading as="h2" size="small" className={styles.noWrapHeading}>
              About our Beta program
            </Heading>
            <Text color="subtle">
              Connect with other beta testers, share feedback, and get early
              updates on new features.
            </Text>
            <Flex gap="xl" direction="column" fullWidth>
              <SettingsRow id="discord-community">
                <SettingsRow.Header>
                  <SettingsRow.Title as="h3" size="xs">
                    Discord community
                  </SettingsRow.Title>
                  <SettingsRow.Description>
                    Join our Discord community to connect with the Buffer team
                    and other Buffer Beta users.
                  </SettingsRow.Description>
                </SettingsRow.Header>
                <SettingsRow.Control>
                  {(ids) => (
                    <Button
                      variant="secondary"
                      size="medium"
                      aria-describedby={ids.descriptionId}
                      onClick={handleJoinDiscord}
                    >
                      <DiscordIcon /> Join Us on Discord
                    </Button>
                  )}
                </SettingsRow.Control>
              </SettingsRow>
              <SettingsRow id="roadmap">
                <SettingsRow.Header>
                  <SettingsRow.Title as="h3" size="xs">
                    Our Roadmap
                  </SettingsRow.Title>
                  <SettingsRow.Description>
                    As you try out our Beta features, we&apos;d love to hear
                    your feedback and get your help in shaping Buffer.
                  </SettingsRow.Description>
                </SettingsRow.Header>
                <SettingsRow.Control>
                  {(ids) => (
                    <Button
                      variant="secondary"
                      size="medium"
                      aria-describedby={ids.descriptionId}
                      onClick={handleCheckRoadmap}
                    >
                      Check Our Roadmap
                    </Button>
                  )}
                </SettingsRow.Control>
              </SettingsRow>
            </Flex>
          </Flex>
        </>
      )}
    </NewSettingsPageLayout>
  )
}

export default BetaFeatures
